import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';
import { venueService } from '../services/api';

const badgeStyles = {
  current: {
    background: '#f3f4f6',
    color: '#475569',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 11,
  },
  empty: {
    background: '#fff7ed',
    color: '#c2410c',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 11,
  },
};

const ImpactSummaryCard = ({
  title,
  accent = '#1d4ed8',
  background = '#eff6ff',
  border = '#bfdbfe',
  count,
  countLabel,
  body,
  loading = false,
}) => (
  <div style={{ background, border: `1px solid ${border}`, borderRadius: 12, padding: '14px 16px' }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: accent, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
      {title}
    </div>
    {loading ? (
      <div style={{ fontSize: 14, color: '#64748b' }}>Calcolo in corso…</div>
    ) : (
      <>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 36, lineHeight: 1, fontWeight: 800, color: accent }}>
            {count}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            {countLabel}
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
          {body}
        </div>
      </>
    )}
  </div>
);

const VenueEditFields = ({
  formData,
  onChange,
  currentCategoryId = null,
  currentCategoryName = null,
  tenantName = null,
  venueId = null,
  venueName = null,
  categories = [],
  onVenueUpdated,
  onPendingCategorySelectionChange,
  pendingCategorySelectionResetKey = 0,
  clearNotice,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [associateConfirmOpen, setAssociateConfirmOpen] = useState(false);
  const [associateNotifyWs, setAssociateNotifyWs] = useState(true);
  const [associateImpactPreview, setAssociateImpactPreview] = useState(null);
  const [associateImpactLoading, setAssociateImpactLoading] = useState(false);
  const [associateSaving, setAssociateSaving] = useState(false);
  const [disassociateConfirmOpen, setDisassociateConfirmOpen] = useState(false);
  const [disassociateNotifyWs, setDisassociateNotifyWs] = useState(true);
  const [disassociateImpactPreview, setDisassociateImpactPreview] = useState(null);
  const [disassociateImpactLoading, setDisassociateImpactLoading] = useState(false);
  const [disassociateSaving, setDisassociateSaving] = useState(false);

  const handleFieldChange = (field, value) => {
    onChange(field, value);
    clearNotice?.();
  };

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === selectedCategoryId) || null,
    [categories, selectedCategoryId]
  );

  useEffect(() => {
    setSelectedCategoryId('');
    setAssociateConfirmOpen(false);
    setDisassociateConfirmOpen(false);
    setAssociateImpactPreview(null);
    setDisassociateImpactPreview(null);
  }, [venueId, currentCategoryId]);

  useEffect(() => {
    setSelectedCategoryId('');
  }, [pendingCategorySelectionResetKey]);

  useEffect(() => {
    onPendingCategorySelectionChange?.({
      hasPendingCategorySelection: !currentCategoryId && Boolean(selectedCategoryId),
      categoryId: selectedCategoryId || null,
      categoryName: selectedCategory?.name || null,
    });
  }, [currentCategoryId, onPendingCategorySelectionChange, selectedCategory, selectedCategoryId]);

  useEffect(() => {
    if (!venueId || !associateConfirmOpen || !selectedCategoryId) return;

    setAssociateImpactLoading(true);
    apiService.getVenueCategoryAssociationImpactPreview(selectedCategoryId, [venueId])
      .then((response) => setAssociateImpactPreview(response.data || null))
      .catch(() => setAssociateImpactPreview(null))
      .finally(() => setAssociateImpactLoading(false));
  }, [venueId, associateConfirmOpen, selectedCategoryId]);

  useEffect(() => {
    if (!venueId || !disassociateConfirmOpen || !currentCategoryId) return;

    setDisassociateImpactLoading(true);
    apiService.getVenueCategoryAssociationImpactPreview(currentCategoryId, [venueId])
      .then((response) => setDisassociateImpactPreview(response.data || null))
      .catch(() => setDisassociateImpactPreview(null))
      .finally(() => setDisassociateImpactLoading(false));
  }, [venueId, disassociateConfirmOpen, currentCategoryId]);

  const handleAssociate = async () => {
    if (!venueId || !selectedCategoryId) return;

    try {
      setAssociateSaving(true);
      clearNotice?.();
      const response = await venueService.updateVenue(venueId, {
        venueCategoryId: selectedCategoryId,
        notifyWs: associateNotifyWs,
      });
      setAssociateConfirmOpen(false);
      setSelectedCategoryId('');
      onVenueUpdated?.(response?.venue || null);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setAssociateSaving(false);
    }
  };

  const handleDisassociate = async () => {
    if (!venueId || !currentCategoryId) return;

    try {
      setDisassociateSaving(true);
      clearNotice?.();
      const response = await venueService.updateVenue(venueId, {
        venueCategoryId: null,
        notifyWs: disassociateNotifyWs,
      });
      setDisassociateConfirmOpen(false);
      onVenueUpdated?.(response?.venue || null);
    } catch (error) {
      alert(error.response?.data?.error || error.message);
    } finally {
      setDisassociateSaving(false);
    }
  };

  const canManageCategory = Boolean(venueId);

  return (
    <>
      {tenantName && (
        <div className="form-group">
          <label>🏢 Tenant</label>
          <input
            type="text"
            value={tenantName}
            readOnly
            className="form-input"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>
      )}

      <div className="form-group">
        <label>🏪 Nome Locale</label>
        <input
          type="text"
          className="form-input"
          value={formData?.name || ''}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          placeholder="Es: Bar Centrale"
        />
      </div>

      <div className="form-group">
        <label>📍 Indirizzo</label>
        <input
          type="text"
          className="form-input"
          value={formData?.address || ''}
          onChange={(e) => handleFieldChange('address', e.target.value)}
          placeholder="Es: Via Roma 10, Milano"
        />
      </div>

      <div className="form-group">
        <label>🏷️ Categoria Venue</label>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {currentCategoryName && (
            <span style={badgeStyles.current}>Attuale: {currentCategoryName}</span>
          )}
          {!currentCategoryName && (
            <span style={badgeStyles.empty}>Nessuna categoria associata</span>
          )}
        </div>
        <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
          {canManageCategory ? (
            currentCategoryId ? (
              <>
                <div style={{ background: '#ffffff', border: '1px solid #dbe4f0', borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>
                    Categoria corrente
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                        {currentCategoryName}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setDisassociateConfirmOpen(true)}
                      style={{ color: '#b91c1c', borderColor: '#fecaca', background: '#fff5f5', fontWeight: 600, padding: '8px 14px' }}
                    >
                      Rimuovi
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  Per assegnare una nuova categoria devi prima <strong>rimuovere</strong> quella attuale. Dopo la rimozione, il locale tornerà senza categoria e potrai fare una nuova associazione.
                </div>
              </>
            ) : (
              <>
                Questo locale non ha una categoria associata. Seleziona la categoria da collegare: la conferma mostrerà l'impatto sulle eventuali configurazioni remote <strong>VENUE_CATEGORY</strong>.
                <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                  <select
                    className="form-input"
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                  >
                    <option value="">Seleziona categoria da associare</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!selectedCategoryId}
                      onClick={() => setAssociateConfirmOpen(true)}
                    >
                      Associa categoria
                    </button>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      Dopo la selezione, devi cliccare <strong>Associa categoria</strong> per confermare questa modifica.
                    </span>
                  </div>
                </div>
              </>
            )
          ) : (
            <>
              La categoria si assegna solo dopo la creazione del locale. Salva prima il nuovo locale, poi rientra in modifica per gestire associazione o disassociazione con i modali di impatto.
            </>
          )}
        </div>
      </div>

      {canManageCategory && selectedCategory && associateConfirmOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(15, 23, 42, 0.52)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            width: '100%', maxWidth: 620, background: '#fff', borderRadius: 18,
            border: '1px solid #dbe4f0', boxShadow: '0 28px 90px rgba(15, 23, 42, 0.28)', overflow: 'hidden',
          }}>
            <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)', borderBottom: '1px solid #dbeafe' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px', borderRadius: 999, background: '#ffffffcc', border: '1px solid #bfdbfe', fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 10 }}>
                🔗 Conferma associazione
              </div>
              <h3 style={{ margin: 0, color: '#1e3a8a' }}>Associare “{venueName || 'questo locale'}” a “{selectedCategory.name}”?</h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#1d4ed8', lineHeight: 1.6 }}>
                Il locale inizierà a ereditare le configurazioni pubblicate con scope <strong>VENUE_CATEGORY</strong> per questa categoria.
              </p>
            </div>

            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <ImpactSummaryCard
                title="Dispositivi coinvolti"
                count={associateImpactPreview?.publishedNamespaceCount > 0 ? (associateImpactPreview?.impactedActiveDeviceCount ?? 0) : 0}
                countLabel={(associateImpactPreview?.publishedNamespaceCount > 0 ? (associateImpactPreview?.impactedActiveDeviceCount ?? 0) : 0) === 1 ? 'device impattato' : 'device impattati'}
                loading={associateImpactLoading}
                body={associateImpactPreview
                  ? associateImpactPreview.publishedNamespaceCount > 0
                    ? <>La categoria <strong>{selectedCategory.name}</strong> ha <strong>{associateImpactPreview.publishedNamespaceCount}</strong> configurazion{associateImpactPreview.publishedNamespaceCount === 1 ? 'e' : 'i'} <strong>VENUE_CATEGORY</strong> pubblicat{associateImpactPreview.publishedNamespaceCount === 1 ? 'a' : 'e'} e questi impatti riguardano il locale che stai associando.</>
                    : <>Per questa categoria non ci sono configurazioni <strong>VENUE_CATEGORY</strong> pubblicate: il locale verrà associato, ma non ci saranno impatti immediati da config di categoria.</>
                  : <>Impossibile determinare l'impatto attuale delle configurazioni di categoria.</>}
              />
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: associateNotifyWs ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={associateNotifyWs}
                    onChange={(e) => setAssociateNotifyWs(e.target.checked)}
                    style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e3a5f' }}>⚡ Notifica istantanea via WebSocket</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                      Se attiva, i device online del locale ricalcolano subito la configurazione. Se disattiva, l'allineamento avverrà solo tramite heartbeat o startup sync.
                    </div>
                  </div>
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#4b5563', lineHeight: 1.6 }}>
                  {associateNotifyWs
                    ? <><strong>WebSocket attivo.</strong> I device online del locale riceveranno subito una notifica e rieseguiranno la risoluzione delle configurazioni remote pubblicate con scope <strong>VENUE_CATEGORY</strong>. I device offline si aggiorneranno comunque al prossimo heartbeat o startup.</>
                    : <><strong>Solo heartbeat/startup sync.</strong> Nessuna push immediata verrà inviata. Il locale si riallineerà al prossimo controllo periodico o al riavvio dell'app.</>}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <button className="btn" onClick={() => setAssociateConfirmOpen(false)} disabled={associateSaving}>
                  Annulla
                </button>
                <button className="btn btn-primary" onClick={handleAssociate} disabled={associateSaving}>
                  {associateSaving ? 'Associazione...' : 'Conferma associazione'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canManageCategory && currentCategoryId && disassociateConfirmOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(15, 23, 42, 0.52)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            width: '100%', maxWidth: 560, background: '#fff', borderRadius: 18,
            border: '1px solid #dbe4f0', boxShadow: '0 28px 90px rgba(15, 23, 42, 0.28)', overflow: 'hidden',
          }}>
            <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 100%)', borderBottom: '1px solid #fde2e8' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px', borderRadius: 999, background: '#ffffffcc', border: '1px solid #fed7aa', fontSize: 12, fontWeight: 700, color: '#9a3412', marginBottom: 10 }}>
                🔓 Disassociazione locale
              </div>
              <h3 style={{ margin: 0, color: '#7c2d12' }}>Disassociare “{venueName || 'questo locale'}”?</h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9a3412', lineHeight: 1.6 }}>
                Il locale verrà rimosso dalla categoria <strong>{currentCategoryName}</strong>.
              </p>
            </div>

            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <ImpactSummaryCard
                title="Dispositivi coinvolti"
                accent="#b45309"
                background="#fff7ed"
                border="#fed7aa"
                count={disassociateImpactPreview?.publishedNamespaceCount > 0 ? (disassociateImpactPreview?.impactedActiveDeviceCount ?? 0) : 0}
                countLabel={(disassociateImpactPreview?.publishedNamespaceCount > 0 ? (disassociateImpactPreview?.impactedActiveDeviceCount ?? 0) : 0) === 1 ? 'device impattato' : 'device impattati'}
                loading={disassociateImpactLoading}
                body={disassociateImpactPreview
                  ? disassociateImpactPreview.publishedNamespaceCount > 0
                    ? <>Dopo la disassociazione, questo locale smetterà di ereditare le configurazioni <strong>VENUE_CATEGORY</strong> pubblicate per <strong>{currentCategoryName}</strong>.</>
                    : <>Per questa categoria non ci sono configurazioni <strong>VENUE_CATEGORY</strong> pubblicate: la disassociazione non produrrà impatti immediati da config di categoria.</>
                  : <>Impossibile determinare l'impatto attuale delle configurazioni di categoria.</>}
              />
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: disassociateNotifyWs ? '#fff7ed' : '#fff', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={disassociateNotifyWs}
                    onChange={(e) => setDisassociateNotifyWs(e.target.checked)}
                    style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: '#d97706', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#7c2d12' }}>⚡ Notifica istantanea via WebSocket</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                      Se attiva, i device online ricalcolano subito il fallback corretto. Se disattiva, il riallineamento avverrà solo tramite heartbeat o startup sync.
                    </div>
                  </div>
                </label>
                <div style={{ padding: '10px 14px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#4b5563', lineHeight: 1.6 }}>
                  {disassociateNotifyWs
                    ? <><strong>WebSocket attivo.</strong> I device online del locale ricalcolano subito la prossima configurazione disponibile in ordine di priorità: <strong>VENUE</strong>, poi <strong>TENANT</strong>, poi <strong>GLOBAL</strong>.</>
                    : <><strong>Solo heartbeat/startup sync.</strong> Nessuna push immediata: il locale abbandonerà la config di categoria al prossimo heartbeat o al prossimo avvio.</>}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <button className="btn" onClick={() => setDisassociateConfirmOpen(false)} disabled={disassociateSaving}>
                  Annulla
                </button>
                <button className="btn" onClick={handleDisassociate} disabled={disassociateSaving} style={{ background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' }}>
                  {disassociateSaving ? 'Disassociazione...' : 'Conferma disassociazione'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VenueEditFields;